import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  ArrowLeft, 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  User, 
  CreditCard, 
  CheckCircle,
  Repeat,
  Star,
  MessageSquare,
  Upload,
  Calculator,
  TrendingUp,
  Package,
  Shield,
  Zap
} from "lucide-react";
import { format, addDays, addWeeks, addMonths, isSameDay } from "date-fns";
import { cn, getPersistedUtm, getFirstTouchUtm, getLastTouchUtm } from "@/lib/utils";
import { getReferralCode } from "@/lib/referral";
import PropertyProfileSelector, { PropertyProfile } from "@/components/booking/PropertyProfileSelector";
import PriceBreakdownAccordion from "@/components/booking/PriceBreakdownAccordion";
import InvoiceBillingForm, { validateBillingInfo } from "@/components/booking/InvoiceBillingForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
  import { applyBounds, arePricingRulesActive, computeServiceTotal, computeSizeMultiplier, getTypeMultiplier, normalizePricingRules, type PricingRulesV1 } from "@/lib/pricingRules";
import { Seo } from "@/components/seo/Seo";
import { pushGtmEvent } from "@/lib/analytics";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { BookingPolicyV2 } from "@/lib/bookingPolicy";
import { describeUnavailableReason } from "@/lib/bookingOfferMessages";

const e164Regex = /^\+?[1-9]\d{7,14}$/;

const bookingSchema = z.object({
  date: z.date({
    required_error: "Seleziona una data.",
  }),
  time: z.string({
    required_error: "Seleziona un orario.",
  }),
  firstName: z.string().min(1, "Il nome è obbligatorio"),
  lastName: z.string().min(1, "Il cognome è obbligatorio"),
  email: z.string().email("Inserisci un'email valida (nome@dominio)"),
  phone: z
    .string()
    .regex(e164Regex, "Inserisci un telefono valido (es. +391234567890)")
    .transform((s) => s.trim()),
  serviceAddress: z
    .string()
    .min(10, "L'indirizzo del servizio è troppo corto")
    .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), {
      message: "Includi via e numero (es. Via Roma 10)",
    }),
  // When useSameAddress is true, billing is copied from serviceAddress in UI — must not require this field
  // or final submit fails (step 3 only validates billingAddress when !useSameAddress).
  billingAddress: z.string().optional().default(""),
  useSameAddress: z.boolean().default(false),
  notes: z.string().optional(),
  // Recurring booking fields
  isRecurring: z.boolean().default(false),
  recurringFrequency: z.enum(["weekly", "biweekly", "monthly"]).optional(),
  recurringEndDate: z.date().optional(),
  // Custom service fields
  squareMeters: z.number().min(1, "I metri quadrati sono obbligatori").optional(),
  // Allow custom service options configured by the company (pricing_rules.types.custom)
  serviceType: z.string().default("standard"),
  urgency: z.enum(["normal", "urgent", "same_day"]).default("normal"),
  // Additional preferences
  preferredCleaner: z.string().optional(),
  specialInstructions: z.string().optional(),
  photos: z.array(z.string()).optional(),
  paymentMethod: z.enum(["card","cash","invoice"]).default("card"),
  requestInvoice: z.boolean().default(false),
  saveBillingToProfile: z.boolean().default(false),
  billingType: z.enum(["private","company"]).optional(),
  billingInfo: z.any().optional(),
}).superRefine((data, ctx) => {
  if (!data.useSameAddress) {
    const b = (data.billingAddress || "").trim();
    if (b.length < 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["billingAddress"],
        message: "L'indirizzo di fatturazione è troppo corto",
      });
    } else if (!/[A-Za-z]/.test(b) || !/\d/.test(b)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["billingAddress"],
        message: "Includi via e numero (es. Via Roma 10)",
      });
    }
  }
  if (data.isRecurring) {
    if (!data.recurringFrequency) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recurringFrequency"], message: "La frequenza è obbligatoria per le prenotazioni ricorrenti" });
    }
    if (!data.recurringEndDate) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recurringEndDate"], message: "La data di fine è obbligatoria per le prenotazioni ricorrenti" });
    }
    if (data.recurringEndDate && data.date && data.recurringEndDate < data.date) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recurringEndDate"], message: "La data di fine deve essere successiva a quella di inizio" });
    }
  }
  const needsBilling = data.paymentMethod === "invoice" || data.requestInvoice;
  if (needsBilling) {
    const billing =
      data.billingInfo && typeof data.billingInfo === "object" && (data.billingInfo as any).type
        ? data.billingInfo
        : { type: data.billingType || "private", ...(typeof data.billingInfo === "object" && data.billingInfo ? data.billingInfo : {}) };
    const err = validateBillingInfo(billing);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["billingInfo"], message: err });
  }
});

type BookingFormData = z.infer<typeof bookingSchema>;

// Time slot options (fixed windows)
const timeSlots = [
  "08:00 - 12:00",
  "12:00 - 16:00",
  "16:00 - 20:00",
];

const baseServiceTypes = [
  { value: "standard", label: "Standard", description: "Servizio di pulizia regolare" },
  { value: "deep_clean", label: "Deep Clean", description: "Pulizia approfondita e accurata" },
  { value: "eco_friendly", label: "Eco‑Friendly", description: "Pulizia attenta all'ambiente" },
  { value: "premium", label: "Premium", description: "Pulizia con prodotti premium" },
];

// urgency options are derived from service.pricing_rules (fallbacks in normalizePricingRules)

function bookingModeLabel(mode: string | undefined): string {
  switch (mode) {
    case "instant":
      return "Conferma immediata";
    case "request":
      return "Richiesta al vendor";
    case "urgent":
      return "Urgente";
    default:
      return mode || "—";
  }
}

// Feature flag: hide the "Richiedi un preventivo" CTA + modal until the
// end-to-end quote flow is finalized. Flip to true to bring it back.
const QUOTES_ENABLED = false;
const GUEST_OFFLINE_PAYMENT_TITLE = "Accedi per usare questo metodo";
const GUEST_OFFLINE_PAYMENT_DESCRIPTION =
  "Per tutelare l'azienda, contanti e fattura sono disponibili solo agli utenti registrati. Accedi oppure scegli il pagamento con carta.";

const BookingForm = () => {
  const { companyId, serviceId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!companyId || !serviceId) return;
    pushGtmEvent("start_booking", {
      company_id: companyId,
      service_id: serviceId,
      booking_flow: "standard",
    });
  }, [companyId, serviceId]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState("standard");
  const [selectedUrgency, setSelectedUrgency] = useState("normal");
  const [squareMeters, setSquareMeters] = useState([100]);
  const [selectedProperty, setSelectedProperty] = useState<PropertyProfile | null>(null);
  const [selectedServiceAddressId, setSelectedServiceAddressId] = useState<string | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteSqm, setQuoteSqm] = useState<number | undefined>(undefined);
  const [quoteNotes, setQuoteNotes] = useState("");
  const [quoteFiles, setQuoteFiles] = useState<File[]>([]);
  const totalSteps = 4;
  const [guestMode, setGuestMode] = useState(false);
  const [acceptedPaymentDisclosure, setAcceptedPaymentDisclosure] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();
  const [offers, setOffers] = useState<any[]>([]);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [offerCheck, setOfferCheck] = useState<{ loading: boolean; data: Record<string, unknown> | null }>({
    loading: false,
    data: null,
  });

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      useSameAddress: false,
      notes: "",
      isRecurring: false,
      serviceType: "standard",
      urgency: "normal",
      squareMeters: 100,
      paymentMethod: "card",
      requestInvoice: false,
      saveBillingToProfile: false,
      billingType: "private",
      billingInfo: undefined,
    },
  });

  const watchDate = form.watch("date");
  const watchTime = form.watch("time");

  const useSameAddress = form.watch("useSameAddress");

  // Keep billing in sync when "same as service" is on (checkbox used to only update React state, not RHF → Zod always saw useSameAddress=false).
  const watchedServiceAddress = form.watch("serviceAddress");
  useEffect(() => {
    if (useSameAddress && watchedServiceAddress) {
      form.setValue("billingAddress", watchedServiceAddress);
    }
  }, [useSameAddress, watchedServiceAddress, form]);

  // Load real company and service by IDs
  const [company, setCompany] = useState<any>({
    id: companyId,
    name: "",
    image: "",
    rating: 4.8,
    reviews: 0,
    responseTime: "2 hours",
  });
  const [service, setService] = useState<any>({ id: serviceId, name: "", base_price: 0, description: "", included_items: [], benefit_items: [] });
  const [pricingRules, setPricingRules] = useState<PricingRulesV1>(() => normalizePricingRules({}));
  
  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        const sb: any = supabase;
        if (companyId) {
          const { data: c } = await sb
            .from("companies")
            .select("id, company_name, logo_url, rating, total_reviews")
            .eq("id", companyId)
            .eq("is_active", true)
            .is("is_deleted", false)
            .eq("share_enabled", true)
            .limit(1);
          const row = c?.[0];
          if (row && isActive) setCompany({
            id: row.id,
            name: row.company_name,
            image: row.logo_url,
            rating: typeof row.rating === 'number' ? row.rating : 0,
            reviews: row.total_reviews || 0,
            responseTime: "2 hours",
          });
        }
        if (serviceId) {
          const { data: s } = await sb
            .from("services")
            .select("id, name, description, base_price, included_items, benefit_items, allowed_service_types, pricing_rules, booking_policy, accepted_payment_methods, vendor_covers_customer_fee, display_fee_disclaimer")
            .eq("id", serviceId)
            .eq("is_active", true)
            .is("is_deleted", false)
            .eq("is_public", true)
            .limit(1);
          const row = s?.[0];
          if (row && isActive) {
            setService({
              id: row.id,
              name: row.name,
              description: row.description,
              base_price: Number(row.base_price) || 0,
              included_items: row.included_items || [],
              benefit_items: row.benefit_items || [],
              allowed_service_types: row.allowed_service_types || [],
              pricing_rules: row.pricing_rules || {},
              booking_policy: (row.booking_policy || {}) as BookingPolicyV2,
              accepted_payment_methods: row.accepted_payment_methods || ["card", "cash"],
              vendor_covers_customer_fee: !!row.vendor_covers_customer_fee,
              display_fee_disclaimer: !!row.display_fee_disclaimer,
            });
            setPricingRules(normalizePricingRules(row.pricing_rules || {}));
          }
        }
        // Load vendor availability map for the current month (to disable fully blocked days)
        try {
          if (companyId) {
            const start = new Date(); start.setDate(1);
            const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
            const { data: cap } = await (sb as any)
              .from('vendor_availability')
              .select('date, capacity')
              .eq('company_id', companyId)
              .gte('date', start.toISOString().slice(0,10))
              .lte('date', end.toISOString().slice(0,10));
            const map: Record<string, { cap: number; count: number }> = {};
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              map[format(d, 'yyyy-MM-dd')] = { cap: 5, count: 0 };
            }
            (cap || []).forEach((r: any) => {
              const k = r.date; if (!map[k]) map[k] = { cap: 5, count: 0 };
              map[k].cap = (typeof r.capacity === 'number' ? Number(r.capacity) : 5);
            });
            // Count existing non-cancelled bookings per date
            const { data: counts } = await (sb as any)
              .from('bookings')
              .select('scheduled_date, status')
              .eq('company_id', companyId)
              .gte('scheduled_date', format(start, 'yyyy-MM-dd'))
              .lte('scheduled_date', format(end, 'yyyy-MM-dd'));
            (counts || []).forEach((b: any) => {
              if (String(b.status) !== 'cancelled') {
                const k = b.scheduled_date; if (!map[k]) map[k] = { cap: 5, count: 0 };
                map[k].count += 1;
              }
            });
            // @ts-ignore
            (window as any).__fh_cap_map__ = map;
          }
        } catch {}
      } catch (_) {}
    })();
    return () => { isActive = false; };
  }, [companyId, serviceId]);

  // Keep selected offer valid when list changes
  useEffect(() => {
    if (offers && offers.length > 0) {
      if (!selectedOfferId || !offers.some((o: any) => o?.id === selectedOfferId)) {
        setSelectedOfferId(offers[0]?.id || null);
      }
    } else if (selectedOfferId) {
      setSelectedOfferId(null);
    }
  }, [offers]);

  // Load active seasonal offers for this company/service (regardless of date)
  useEffect(() => {
    (async () => {
      try {
        if (!companyId || !serviceId) return;
        const { data, error } = await (supabase as any)
          .from('seasonal_offers')
          .select('id, name, discount_percent, start_date, end_date')
          .eq('company_id', companyId)
          .eq('service_id', serviceId)
          .eq('is_active', true)
          .order('discount_percent', { ascending: false });
        if (error) throw error;
        setOffers(data || []);
        if ((data || []).length > 0 && !selectedOfferId) {
          setSelectedOfferId(data![0].id);
        }
      } catch {}
    })();
  }, [companyId, serviceId]);

  useEffect(() => {
    if (!companyId || !serviceId) return;
    const d = watchDate;
    const tm = watchTime;
    if (!d || !tm) {
      setOfferCheck({ loading: false, data: null });
      return;
    }
    let cancelled = false;
    setOfferCheck((s) => ({ ...s, loading: true }));
    (async () => {
      const scheduledTime = tm.includes(" - ") ? tm.split(" - ")[0] : tm;
      const { data, error } = await (supabase as any).rpc("resolve_booking_offer", {
        p_company_id: companyId,
        p_service_id: serviceId,
        p_scheduled_date: format(d, "yyyy-MM-dd"),
        p_scheduled_time: scheduledTime,
      });
      if (cancelled) return;
      if (error) {
        setOfferCheck({ loading: false, data: { ok: false, unavailable_reason: error.message } });
        return;
      }
      setOfferCheck({ loading: false, data: (data || null) as Record<string, unknown> });
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, serviceId, watchDate, watchTime]);

  // Load saved billing info from profile when user requests invoice (logged-in only)
  const requestInvoice = form.watch("requestInvoice");
  const paymentMethod = form.watch("paymentMethod");
  useEffect(() => {
    if (!requestInvoice && paymentMethod !== "invoice") return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      const { data: prof } = await supabase.from("profiles").select("billing_info").eq("user_id", session.user.id).maybeSingle();
      const saved = (prof as any)?.billing_info as import("@/components/booking/InvoiceBillingForm").BillingInfo | null | undefined;
      if (saved?.type && !form.getValues("billingInfo")) {
        form.setValue("billingType", saved.type);
        form.setValue("billingInfo", saved);
      }
    })();
  }, [requestInvoice, paymentMethod]);

  // Pre-fill contact fields from the logged-in user's profile so customer_name is stored correctly
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id || guestMode) return;
      const { data: prof } = await (supabase as any)
        .from('profiles')
        .select('full_name, email, phone')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (!prof) return;
      const full = String(prof.full_name || '').trim();
      const parts = full ? full.split(/\s+/) : [];
      if (parts.length >= 1 && !form.getValues('firstName')) {
        form.setValue('firstName', parts[0]);
        form.setValue('lastName', parts.slice(1).join(' ') || '');
      }
      if (prof.email && !form.getValues('email')) {
        form.setValue('email', prof.email);
      }
      if (prof.phone && !form.getValues('phone')) {
        form.setValue('phone', prof.phone);
      }
    })();
  }, [guestMode]);

  /**
   * Single source of truth for the service total. Delegates to
   * computeServiceTotal so BookingForm sidebar, the price breakdown
   * accordion and the checkout total agree exactly. Urgency is no
   * longer chosen by the customer: it is derived exclusively from
   * offerCheck.data.booking_mode === "urgent" (server-resolved from
   * the date/time picked in step 1).
   */
  const getRawPrice = () => {
    const basePrice = Number(service?.base_price || 0) || 0;
    const rr = normalizePricingRules((service as any)?.pricing_rules || pricingRules || {});
    const pol = ((service as any)?.booking_policy || {}) as BookingPolicyV2;
    const offer = offerCheck.data;
    const isUrgent = !!(offer && offer.ok === true && offer.booking_mode === "urgent");
    const urgencyMultiplier = isUrgent
      ? (Number(pol?.urgent?.pricing_value) || 1.5)
      : 1;
    const result = computeServiceTotal({
      basePrice,
      rules: rr,
      pricingType: (service as any)?.pricing_type,
      sqm: squareMeters[0],
      urgencyMultiplier,
      serviceTypeKey: selectedServiceType,
    });
    return result.total;
  };

  const getSelectedOffer = () => offers.find(o => o.id === selectedOfferId) || null;
  const getSelectedDate = () => form.getValues('date') as Date | undefined;
  const isOfferValidForDate = (offer: any): boolean => {
    const d = getSelectedDate();
    if (!offer || !offer.start_date || !offer.end_date || !d) return false;
    const cur = format(d, 'yyyy-MM-dd');
    return String(cur) >= String(offer.start_date) && String(cur) <= String(offer.end_date);
  };
  const getApplicableOffer = () => {
    const offer = getSelectedOffer();
    return offer && isOfferValidForDate(offer) ? offer : null;
  };

  const calculatePrice = () => {
    const raw = getRawPrice();
    const offer = getApplicableOffer();
    const pct = offer ? Math.max(0, Math.min(100, Number(offer.discount_percent) || 0)) : 0;
    const discounted = Math.round(raw * (1 - pct / 100));
    return Math.max(0, discounted);
  };

  // Recurring helpers
  const getOccurrencesCount = () => {
    if (!isRecurring) return 1;
    const start = form.watch("date");
    const end = form.watch("recurringEndDate");
    const freq = form.watch("recurringFrequency");
    if (!start || !end || !freq) return 1;
    let count = 0;
    let cursor = new Date(start);
    while (cursor <= end) {
      count += 1;
      if (freq === "weekly") cursor = addWeeks(cursor, 1);
      else if (freq === "biweekly") cursor = addWeeks(cursor, 2);
      else cursor = addMonths(cursor, 1);
    }
    return count;
  };
  const getSeriesTotal = () => calculatePrice() * getOccurrencesCount();

  const getUrgencyOptions = () => {
    const rr = normalizePricingRules((service as any)?.pricing_rules || pricingRules || {});
    return [
      { value: "normal", label: "Standard (2‑3 giorni)", multiplier: rr.urgency.normal || 1 },
      { value: "urgent", label: "Urgente (24 ore)", multiplier: rr.urgency.urgent || 1.3 },
      { value: "same_day", label: "In giornata", multiplier: rr.urgency.same_day || 1.5 },
    ];
  };

  const getServiceTypeOptions = () => {
    const rr = normalizePricingRules((service as any)?.pricing_rules || pricingRules || {});
    const allowed: string[] = Array.isArray((service as any)?.allowed_service_types) ? (service as any).allowed_service_types : [];
    const base = baseServiceTypes
      .filter((st) => allowed.length === 0 || allowed.includes(st.value))
      .map((st) => ({ ...st, multiplier: getTypeMultiplier(st.value, rr.types) }));
    const custom = (rr.types.custom || []).map((c) => ({ value: c.key, label: c.label, description: c.description || "Opzione personalizzata", multiplier: c.multiplier }));
    return [...base, ...custom];
  };

  const onSubmit = async (data: BookingFormData) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      const selectedPaymentMethod = form.getValues().paymentMethod;

      if ((!session || guestMode) && (selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'invoice')) {
        toast({
          title: GUEST_OFFLINE_PAYMENT_TITLE,
          description: GUEST_OFFLINE_PAYMENT_DESCRIPTION,
          variant: 'destructive'
        });
        return;
      }

      // Guard: recurring requires frequency and end date
      if (isRecurring) {
        const freq = form.getValues('recurringFrequency');
        const end = form.getValues('recurringEndDate');
        if (!freq || !end) {
          toast({ title: 'Ricorrenza obbligatoria', description: 'Seleziona frequenza e data di fine per le prenotazioni ricorrenti.', variant: 'destructive' });
          return;
        }
      }

      // Enforce accepted payment methods BEFORE creating any booking
      {
        const svcAccepted = (service?.accepted_payment_methods || []) as string[];
        if (svcAccepted.length > 0 && !svcAccepted.includes(selectedPaymentMethod)) {
          toast({
            title: 'Metodo di pagamento non accettato',
            description: 'Il venditore non accetta il metodo selezionato.',
            variant: 'destructive'
          });
          return;
        }
      }

      // Recurring end date must be after start date to produce >1 occurrence
      if (isRecurring) {
        const startDate = data.date;
        const endDate = form.getValues('recurringEndDate') as Date | null;
        if (!endDate || !startDate || endDate <= startDate) {
          toast({
            title: 'Configurazione ricorrenza non valida',
            description: 'Seleziona una data di fine successiva a quella di inizio per creare più occorrenze.',
            variant: 'destructive'
          });
          return;
        }
      }

      if (offerCheck.loading) {
        toast({
          title: "Verifica in corso",
          description: "Attendi il calcolo della modalità di prenotazione per data e ora selezionati.",
          variant: "destructive",
        });
        return;
      }
      if (offerCheck.data && offerCheck.data.ok === false) {
        toast({
          title: "Combinazione non prenotabile",
          description: "Modifica data o orario oppure riprova più tardi.",
          variant: "destructive",
        });
        return;
      }

      // Always create a booking via RPC (guest or logged-in)
      // Fetch company/service rows to get slugs and flags
      const compRes: any = await (supabase as any)
        .from("companies")
        .select("id, slug, share_enabled")
        .eq("id", companyId)
        .eq("is_active", true)
        .is("is_deleted", false)
        .limit(1);
      const compRows: any[] = compRes?.data || [];
      const compErr = compRes?.error;
      const svcRes: any = await (supabase as any)
        .from("services")
        .select("id, slug, is_public")
        .eq("id", serviceId)
        .eq("is_active", true)
        .is("is_deleted", false)
        .eq("is_public", true)
        .limit(1);
      const svcRows: any[] = svcRes?.data || [];
      const svcErr = svcRes?.error;
      if (compErr || svcErr || !compRows?.length || !svcRows?.length) {
        toast({ title: "Prenotazione non riuscita", description: "Azienda o servizio non trovati", variant: "destructive" });
        return;
      }

      // Preflight vendor readiness (customer-side): only check presence of a connected account.
      // Final capability gating still enforced server-side to avoid false negatives for customers.
      try {
        const pm = form.getValues().paymentMethod;
        if (pm === 'card' && compRows?.[0]?.id) {
          // Check for Stripe account based on mode (test vs live). Do not SELECT legacy
          // `stripe_account_id` — if the column is missing, PostgREST returns 400 for the whole query.
          const { data: compGate, error: compGateErr } = await (supabase as any)
            .from('companies')
            .select('stripe_account_id_test, stripe_account_id_live')
            .eq('id', compRows[0].id)
            .maybeSingle();
          if (compGateErr) {
            toast({
              title: 'Verifica pagamenti',
              description: 'Impossibile leggere lo stato Stripe dell’azienda. Riprova tra poco.',
              variant: 'destructive',
            });
            return;
          }

          // Determine mode: check if we're in test mode (VITE_STRIPE_PUBLISHABLE_KEY starts with pk_test_)
          const isTestMode = typeof window !== 'undefined' &&
            (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').startsWith('pk_test_');

          const hasAccount = isTestMode
            ? !!compGate?.stripe_account_id_test
            : !!compGate?.stripe_account_id_live;
          
          if (!hasAccount) {
            toast({ title: 'Venditore non pronto', description: 'L\'azienda non ha ancora completato la configurazione Stripe necessaria per accettare pagamenti con carta.', variant: 'destructive' });
            return;
          }
          // otherwise proceed; server will enforce the stricter READY criteria before checkout.
        }
      } catch (_) {}

      const companySlug = compRows?.[0]?.slug as string | undefined;
      const serviceSlug = svcRows?.[0]?.slug as string | undefined;
      const canUseSlugs = !!companySlug && !!serviceSlug;

      // Hard gate for card payments: ensure vendor Stripe account is actually ready (capabilities),
      // otherwise DO NOT create a booking.
      {
        const pm = form.getValues().paymentMethod;
        if (pm === 'card' && compRows?.[0]?.id) {
          try {
            const readyResp = await fetch('/api/stripe?action=vendor_ready', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ company_id: compRows[0].id })
            });
            const readyData = await readyResp.json().catch(() => ({}));
            if (!readyResp.ok) {
              toast({
                title: 'Pagamenti non disponibili',
                description: readyData?.error || 'Impossibile verificare lo stato pagamenti del venditore.',
                variant: 'destructive'
              });
              return;
            }
            if (readyData?.vendorReady !== true) {
              const mode = readyData?.mode || 'mode sconosciuta';
              toast({
                title: 'Pagamenti non disponibili',
                description: `L'azienda non e' ancora READY per i pagamenti con carta (${mode}). Completa verifica, payout e requisiti Stripe.`,
                variant: 'destructive'
              });
              return;
            }
          } catch (e: any) {
            toast({
              title: 'Pagamenti non disponibili',
              description: e?.message || 'Impossibile verificare lo stato pagamenti del venditore.',
              variant: 'destructive'
            });
            return;
          }
        }
      }

      // Capacity enforcement: block when vendor capacity is 0 or reached for the selected date
      try {
        const selectedDateIso = data.date ? format(data.date, 'yyyy-MM-dd') : null;
        if (selectedDateIso) {
          const slotLabel = (data.time || "") as string;
          const rawStart = slotLabel.includes(" - ") ? slotLabel.split(" - ")[0] : slotLabel;
          const m = String(rawStart).trim().match(/^(\d{1,2}):(\d{2})$/);
          const slotStart = m
            ? `${String(parseInt(m[1], 10)).padStart(2, "0")}:${m[2]}`
            : String(rawStart).trim().slice(0, 5);

          // If slot-based availability exists for this day, enforce per-slot here (best-effort; DB enforces too)
          const { data: slotAny } = await (supabase as any)
            .from('vendor_availability_slots')
            .select('id')
            .eq('company_id', compRows?.[0]?.id)
            .eq('date', selectedDateIso)
            .limit(1);

          if (Array.isArray(slotAny) && slotAny.length > 0) {
            const { data: slotRow } = await (supabase as any)
              .from('vendor_availability_slots')
              .select('capacity, blocked')
              .eq('company_id', compRows?.[0]?.id)
              .eq('date', selectedDateIso)
              .eq('slot', slotStart)
              .maybeSingle();
            const capacity = Number(slotRow?.capacity ?? 0);
            const blocked = !!slotRow?.blocked;
            if (blocked || capacity <= 0) {
              toast({ title: 'Slot non disponibile', description: 'La fascia oraria selezionata non è disponibile. Scegli un altro slot.', variant: 'destructive' });
              return;
            }
            const bookingsCountResp = await (supabase as any)
              .from('bookings')
              .select('id', { count: 'exact', head: true })
              .eq('company_id', compRows?.[0]?.id)
              .eq('scheduled_date', selectedDateIso)
              .eq('scheduled_time', slotStart)
              .in('status', ['pending','confirmed','in_progress']);
            const countNow = Number(bookingsCountResp?.count || 0);
            if (countNow >= capacity) {
              toast({ title: 'Slot al completo', description: 'La fascia oraria selezionata è al completo. Scegli un altro slot.', variant: 'destructive' });
              return;
            }
          } else {
            // Fallback: per-day capacity (legacy). No row = unknown day-level cap — do not treat as 0 (slots-only vendors may omit this row).
            const { data: capRow } = await (supabase as any)
              .from('vendor_availability')
              .select('capacity, blocked')
              .eq('company_id', compRows?.[0]?.id)
              .eq('date', selectedDateIso)
              .maybeSingle();
            if (capRow != null) {
              const capacity = Number(capRow.capacity ?? 0);
              const blocked = !!capRow.blocked;
              if (blocked || capacity <= 0) {
                toast({ title: 'Giorno non disponibile', description: 'La data selezionata non è disponibile. Scegli un altro giorno.', variant: 'destructive' });
                return;
              }
              const bookingsCountResp = await (supabase as any)
                .from('bookings')
                .select('id', { count: 'exact', head: true })
                .eq('company_id', compRows?.[0]?.id)
                .eq('scheduled_date', selectedDateIso)
                .in('status', ['pending','confirmed','in_progress']);
              const countNow = Number(bookingsCountResp?.count || 0);
              if (countNow >= capacity) {
                toast({ title: 'Giorno al completo', description: 'La data selezionata non è disponibile. Scegli un altro giorno.', variant: 'destructive' });
                return;
              }
            }
          }
        }
      } catch (_) {}

      // Enhanced UTM tracking with first-touch and last-touch attribution
      const firstTouchUtm = getFirstTouchUtm();
      const lastTouchUtm = getLastTouchUtm();
      const currentUtm = getPersistedUtm();
      
      // Combine UTM data for comprehensive tracking
      const utmData = {
        first_touch: firstTouchUtm,
        last_touch: lastTouchUtm,
        current: currentUtm,
        // Legacy format for backward compatibility
        ...currentUtm
      };
      
      const baseTotal = isRecurring ? getSeriesTotal() : calculatePrice();
      const serviceFee = Math.min(Math.round((isRecurring ? calculatePrice() : baseTotal) * 0.02 * 100) / 100, 10); // 2% capped €10; apply per-visit for recurring at checkout
      // If vendor covers customer fee, the displayed price to customer can embed it
      const vendorCoversFee = !!service?.vendor_covers_customer_fee;
      const customerFeeCents = vendorCoversFee ? 0 : Math.round((serviceFee || 0) * 100);
      const customerTotal = vendorCoversFee ? baseTotal : baseTotal + serviceFee;
      const grandTotal = customerTotal;

      const slot = data.time || "";
      const scheduledTime = slot.includes(" - ") ? slot.split(" - ")[0] : slot;
      const refCode = getReferralCode();
      const payload: any = {
        scheduled_date: data.date ? format(data.date, "yyyy-MM-dd") : null,
        scheduled_time: scheduledTime,
        total_amount: grandTotal,
        customer_fee_cents: customerFeeCents,
        status: "pending",
        // IMPORTANT: persist selected payment method to avoid DB defaulting to 'card'
        payment_method: (form.getValues().paymentMethod || 'card'),
        invoice_requested: form.getValues().paymentMethod === 'invoice' || !!form.getValues().requestInvoice,
        billing_info: (form.getValues().paymentMethod === 'invoice' || form.getValues().requestInvoice) ? form.getValues().billingInfo : null,
        // Persist typed addresses for guest bookings (and as fallback even when service_address_id exists)
        service_address_text: (data.serviceAddress || "").trim() || null,
        billing_address_text: (data.useSameAddress
          ? (data.serviceAddress || "").trim()
          : (data.billingAddress || "").trim()) || null,
        utm: utmData,
        // explicit UTM columns for denormalized analytics
        utm_source: currentUtm.utm_source || currentUtm.source || firstTouchUtm.utm_source || firstTouchUtm.source || null,
        utm_medium: currentUtm.utm_medium || currentUtm.medium || firstTouchUtm.utm_medium || firstTouchUtm.medium || null,
        utm_campaign: currentUtm.utm_campaign || currentUtm.campaign || firstTouchUtm.utm_campaign || firstTouchUtm.campaign || null,
        utm_term: currentUtm.utm_term || currentUtm.term || firstTouchUtm.utm_term || firstTouchUtm.term || null,
        utm_content: currentUtm.utm_content || currentUtm.content || firstTouchUtm.utm_content || firstTouchUtm.content || null,
        landing_page: (currentUtm as any).landing_page || (firstTouchUtm as any).landing_page || (typeof window !== 'undefined' ? window.location.href : null),
        referrer: (currentUtm as any).referrer || (firstTouchUtm as any).referrer || (typeof document !== 'undefined' ? document.referrer : null),
        source: currentUtm.utm_source || currentUtm.source || 'direct',
        idempotency_key: `${companyId}-${serviceId}-${session?.user?.id || 'guest'}-${format(new Date(), 'yyyyMMddHHmmss')}`,
        ref_code: refCode || null,
        square_meters: squareMeters[0],
        service_type: selectedServiceType,
        offer_discount_percent: getApplicableOffer()
          ? Math.max(0, Math.min(100, Number(getApplicableOffer()!.discount_percent) || 0))
          : 0,
      };
      if (session && !guestMode) {
        payload.customer_email = session.user?.email || data.email;
        payload.customer_name = `${data.firstName} ${data.lastName}`.trim();
        payload.customer_phone = data.phone;
        // Resolve profile id to populate customer_id; if missing, fall back to guest fields
        const { data: prof, error: profErr } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", session.user.id)
          .maybeSingle();
        if (!profErr && prof?.id) {
          payload.customer_id = prof.id;
        } else {
          // Fallback to guest fields so DB check passes even if profile row missing
          payload.guest_name = `${data.firstName} ${data.lastName}`.trim();
          payload.guest_email = data.email;
          payload.guest_phone = data.phone;
        }
      } else {
        payload.guest_name = `${data.firstName} ${data.lastName}`.trim();
        payload.guest_email = data.email;
        payload.guest_phone = data.phone;
        // Also set customer_* to satisfy NOT NULL constraints in bookings
        payload.customer_name = payload.guest_name;
        payload.customer_email = payload.guest_email;
        payload.customer_phone = payload.guest_phone;
      }

      // If a saved address has been selected and user is authenticated, attach service_address_id
      if (selectedServiceAddressId) {
        payload.service_address_id = selectedServiceAddressId;
      }

      // Recurrence: assign series_id and recurrence metadata on first booking
      if (isRecurring && form.getValues('recurringEndDate') && form.getValues('recurringFrequency')) {
        const seriesId = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
        payload.series_id = seriesId;
        payload.recurrence_frequency = form.getValues('recurringFrequency');
        payload.recurrence_end_date = format(form.getValues('recurringEndDate') as Date, 'yyyy-MM-dd');
      }

      // ─────────────────────────────────────────────────────────────────────
      // NEW DRAFT FLOW for non-recurring CARD payments
      // ─────────────────────────────────────────────────────────────────────
      // Before this change, the booking row was inserted into `bookings` BEFORE
      // Stripe Checkout was opened. That caused stale `pending` rows + admin /
      // customer emails when the user abandoned the Stripe page.
      // Now: for card payments we hand the entire payload to
      // /api/stripe?action=init_draft, which stores a draft and creates the
      // Checkout Session. The booking row is created by the Stripe webhook
      // only after payment succeeds. Cash / invoice / recurring keep the
      // legacy upfront-insert path because they don't go through the webhook.
      const __pmMethod = String(form.getValues().paymentMethod || 'card');
      const __useDraftFlow = !isRecurring && __pmMethod === 'card';
      if (__useDraftFlow) {
        try {
          // Photo upload (deferred-to-finalize) — only for authenticated users with
          // uploaded files. We upload to `draft/<tempId>/...` BEFORE creating the
          // draft, then pass the paths in `payload.draft_photos`. The webhook's
          // finalize_booking_from_draft RPC inserts the booking_photos rows after
          // the booking is created. If finalize aborts (slot lost), the files
          // remain orphan in storage and can be cleaned up by a periodic job.
          const __draftPhotoPaths: { url: string; photo_type: string }[] = [];
          try {
            if (localPhotoFiles.length > 0) {
              const { data: sess } = await supabase.auth.getSession();
              if (sess?.session) {
                const tempDraftId = (typeof crypto !== 'undefined' && crypto.randomUUID)
                  ? crypto.randomUUID()
                  : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                for (const file of localPhotoFiles) {
                  const path = `draft/${tempDraftId}/${Date.now()}_${file.name}`;
                  const up = await (supabase as any).storage.from('booking-photos').upload(path, file, { upsert: false });
                  if (!up.error) {
                    __draftPhotoPaths.push({ url: path, photo_type: 'pre' });
                  }
                }
              }
            }
          } catch (_) {
            // photo upload failure must not block the booking flow
          }
          if (__draftPhotoPaths.length > 0) {
            (payload as any).draft_photos = __draftPhotoPaths;
          }

          const resp = await fetch('/api/stripe?action=init_draft', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              company_id: compRows?.[0]?.id,
              service_id: svcRows?.[0]?.id,
              payload,
              currency: 'eur',
            }),
          });
          const initData = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            const code = initData?.error;
            const mode = initData?.mode;
            const friendly =
              code === 'vendor_not_ready'
                ? `Pagamenti non disponibili: l'azienda non ha completato Stripe (${mode || 'mode sconosciuta'}).`
                : code === 'draft_validation_failed'
                  ? `Validazione prenotazione: ${initData?.message || code}`
                  : code === 'vendor_on_hold'
                    ? 'Il fornitore è temporaneamente sospeso dai pagamenti.'
                    : null;
            const extra = initData?.message || initData?.details?.hint || null;
            const desc = [friendly || code || null, extra].filter(Boolean).join(' ');
            toast({
              title: 'Errore di pagamento',
              description: desc || 'Inizializzazione pagamento non riuscita',
              variant: 'destructive',
            });
            return;
          }
          // Free (€0) booking: the server already finalized it directly.
          if (initData?.free === true) {
            navigate(`/payment-success`, {
              state: {
                bookingId: initData?.booking_id || null,
                bookingNumber: initData?.booking_number || null,
                bookingData: data,
                orderId: `ORD-${Date.now()}`,
                company: { name: company.name, image: company.image },
                service: { name: service.name },
                pricing: { servicePrice: baseTotal, serviceFee, tax: 0, discount: 0, total: grandTotal },
                payment: { method: 'card', status: 'paid', free: true },
              },
            });
            return;
          }
          if (initData?.url) {
            window.location.assign(initData.url);
            return;
          }
          toast({ title: 'Errore di pagamento', description: 'URL di checkout non fornita', variant: 'destructive' });
          return;
        } catch (e: any) {
          toast({ title: 'Errore di pagamento', description: e?.message || 'Inizializzazione pagamento non riuscita', variant: 'destructive' });
          return;
        }
      }
      // ─────────────────────────────────────────────────────────────────────
      // LEGACY UPFRONT-INSERT PATH (recurring / cash / invoice)
      // ─────────────────────────────────────────────────────────────────────
      let rpcError = null as any;
      let parentRecurringBookingId: string | null = null;
      const sbAny = supabase as any;
      // Recurring no longer requires a saved service_address_id — we accept
      // service_address_text (typed by the user) and pass null for the id.
      if (isRecurring && session) {
        try {
          const { data: prof } = await supabase.from("profiles").select("id").eq("user_id", session.user.id).maybeSingle();
          const profileId = prof?.id as string | undefined;
          if (profileId) {
            const { data: seriesParentId, error: recErr } = await sbAny.rpc("create_recurring_booking", {
              p_customer_id: profileId,
              p_company_id: compRows?.[0]?.id,
              p_service_id: svcRows?.[0]?.id,
              p_service_address_id: selectedServiceAddressId || null,
              p_scheduled_date: payload.scheduled_date,
              p_scheduled_time: payload.scheduled_time,
              p_total_amount: Number(payload.total_amount || 0),
              p_customer_fee_cents: customerFeeCents,
              p_recurrence_frequency: form.getValues('recurringFrequency'),
              p_recurrence_end_date: format(form.getValues('recurringEndDate') as Date, 'yyyy-MM-dd'),
              p_notes: payload.notes || null,
              p_pricing_payload: {
                use_server_pricing: true,
                square_meters: squareMeters[0],
                service_type: selectedServiceType,
                offer_discount_percent: getApplicableOffer()
                  ? Math.max(0, Math.min(100, Number(getApplicableOffer()!.discount_percent) || 0))
                  : 0,
                payment_method: form.getValues("paymentMethod") || "card",
              },
            });
            if (recErr) {
              rpcError = recErr;
            } else {
              parentRecurringBookingId = seriesParentId as string;
            }
          } else {
            rpcError = { message: "Unable to resolve customer profile for recurring booking." } as any;
          }
        } catch (e: any) {
          rpcError = e;
        }
      } else {
        if (canUseSlugs) {
          const { error } = await sbAny.rpc("create_public_booking", {
            p_company_slug: companySlug,
            p_service_slug: serviceSlug,
            p_payload: payload,
          });
          rpcError = error;
        } else {
          if (svcRows?.[0]?.is_public) {
            const { error } = await sbAny.rpc("create_public_booking_by_ids", {
              p_company_id: compRows?.[0]?.id,
              p_service_id: svcRows?.[0]?.id,
              p_payload: payload,
            });
            rpcError = error;
          } else {
            rpcError = { message: "This company or service is not publicly shareable yet." } as any;
          }
        }
      }

      let duplicateHandled = false;
      if (rpcError) {
        const msg = String(rpcError.message || '');
        // Slot già occupato: errore esplicito o 409 (unique su company_id + date + time)
        if (/slot_unavailable|uniq_active_company_slot|23505|duplicate key/i.test(msg)) {
          toast({
            title: "Slot non disponibile",
            description: "Questo orario è già occupato. Scegli un'altra data o un altro orario.",
            variant: "destructive",
          });
          return;
        }
        if (/uq_booking_time_tuple/i.test(msg)) {
          // Idempotency: stesso tuple già esistente; riusa booking
          duplicateHandled = true;
        } else {
          toast({ title: "Prenotazione non riuscita", description: rpcError.message, variant: "destructive" });
          return;
        }
      }

      // (Email moved to after booking id retrieval to include ICS link)

      // Retrieve created booking ID to link payments accurately
      // If recurring RPC used, we already have parent id; else fallback to existing lookup paths
      const created = parentRecurringBookingId
        ? { data: [{ id: parentRecurringBookingId, booking_number: null, platform_fee_cents: 0, customer_fee_cents: 0 }] }
        : duplicateHandled
          ? await (supabase as any)
              .from('bookings')
              .select('id, booking_number, platform_fee_cents, customer_fee_cents')
              .eq('company_id', compRows?.[0]?.id)
              .eq('service_id', svcRows?.[0]?.id)
              .eq('scheduled_date', payload.scheduled_date)
              .eq('scheduled_time', payload.scheduled_time)
              .eq('customer_email', payload.customer_email)
              .order('created_at', { ascending: false })
              .limit(1)
          : await (supabase as any)
              .from('bookings')
              .select('id, booking_number, platform_fee_cents, customer_fee_cents')
              .eq('customer_email', payload.customer_email)
              .order('created_at', { ascending: false })
              .limit(1);
      const bookingId = created?.data?.[0]?.id;
      const bookingNumber = created?.data?.[0]?.booking_number || '';
      const platformFeeCents = created?.data?.[0]?.platform_fee_cents || 0;
      const createdCustomerFeeCents = created?.data?.[0]?.customer_fee_cents || 0;

      if (!bookingId) {
        toast({
          title: 'Errore di pagamento',
          description: "Non sono riuscito a recuperare l'ID della prenotazione appena creata. Riprova tra qualche secondo.",
          variant: 'destructive'
        });
        return;
      }

      const rollbackUnpaidCardBooking = async () => {
        try {
          const idem = String(payload?.idempotency_key || '').trim();
          if (!idem) return;
          await fetch('/api/ops?action=booking_rollback_unpaid', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ booking_id: bookingId, idempotency_key: idem })
          });
        } catch {}
      };

      // Enqueue booking_request email (guest or logged-in) with ICS
      try {
        const emailTo = (payload.customer_email || payload.guest_email) as string;
        if (emailTo) {
          const start = new Date(`${payload.scheduled_date}T${payload.scheduled_time || '09:00'}:00`);
          const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
          const dt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
          const ics = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Halomora//Bookings//EN',
            'BEGIN:VEVENT',
            `UID:${bookingId || `${Date.now()}@halomora`}`,
            `DTSTAMP:${dt(new Date())}`,
            `DTSTART:${dt(start)}`,
            `DTEND:${dt(end)}`,
            `SUMMARY:${service.name} - ${company.name}`,
            `DESCRIPTION:${service.name} with ${company.name}`,
            'END:VEVENT',
            'END:VCALENDAR'
          ].join('\r\n');
          await fetch('/api/notifications?action=enqueue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              template: 'booking_request',
              booking_id: bookingId,
              to_email: emailTo,
              sendNow: true,
              payload: {
                customer_name: payload.customer_name || payload.guest_name,
                company_name: company.name,
                service_name: service.name,
                booking_date: payload.scheduled_date,
                booking_time: payload.scheduled_time,
                booking_address: payload.service_address_text || payload.billing_address_text || '',
                booking_total: formatCurrency(grandTotal),
                total_amount: grandTotal,
                booking_number: bookingNumber || `BK-${Date.now()}`,
                manage_link: window.location.origin + '/profile',
                ics_filename: `booking-${bookingId}.ics`,
                ics_content_base64: typeof btoa !== 'undefined' ? btoa(ics) : Buffer.from(ics).toString('base64')
              }
            })
          });
        }
      } catch (_) {}

      const isGuestBooking = !session || guestMode;
      if (isGuestBooking && bookingId) {
        try {
          await fetch('/api/ops?action=guest_prepare', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ booking_id: bookingId }),
          });
        } catch (_) {}
      }

      try {
        const payM = String(form.getValues().paymentMethod || '');
        if (bookingId && (payM === 'cash' || payM === 'invoice')) {
          await fetch('/api/notifications?action=notify_vendor_pending_offline', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ booking_id: bookingId })
          });
        }
      } catch (_) {}

      // If user selected photos, upload to storage and record in booking_photos
      try {
        if (bookingId && localPhotoFiles.length > 0) {
          const { data: sess } = await supabase.auth.getSession();
          if (sess?.session) {
            for (const file of localPhotoFiles) {
              const path = `${bookingId}/${Date.now()}_${file.name}`;
              const up = await (supabase as any).storage.from('booking-photos').upload(path, file, { upsert: false });
              if (!up.error) {
                await (supabase as any).from('booking_photos').insert({ booking_id: bookingId, url: path, photo_type: 'pre' });
              }
            }
          }
        }
      } catch (_) {}

      const method = form.getValues().paymentMethod;
      const saveBillingToProfile = !!form.getValues().saveBillingToProfile;
      if (saveBillingToProfile && (method === 'invoice' || form.getValues().requestInvoice) && payload.billing_info && session?.user?.id) {
        try {
          const { data: prof } = await supabase.from("profiles").select("id").eq("user_id", session.user.id).maybeSingle();
          if (prof?.id) {
            await (supabase as any).from("profiles").update({ billing_info: payload.billing_info, updated_at: new Date().toISOString() }).eq("id", prof.id);
          }
        } catch (_) {}
      }

      if (method === 'invoice') {
        // Solo fattura: nessun pagamento online; l'azienda emetterà fattura e il cliente pagherà con bonifico
        const bookingModeMeta =
          offerCheck.data?.ok === true
            ? {
                booking_mode: offerCheck.data.booking_mode as string | undefined,
                approval_status: offerCheck.data.approval_status as string | undefined,
                vendor_response_deadline_at: offerCheck.data.vendor_response_deadline_at as string | null | undefined,
              }
            : undefined;
        navigate(`/payment-success`, {
          state: {
            bookingId,
            bookingData: data,
            orderId: `ORD-${Date.now()}`,
            company: { name: company.name, image: company.image },
            service: { name: service.name },
            pricing: { servicePrice: baseTotal, serviceFee, tax: 0, discount: 0, total: grandTotal },
            payment: { method: 'invoice', status: 'pending_invoice', message: 'Prenotazione inviata. L\'azienda emetterà la fattura; potrai pagare con bonifico dopo averla ricevuta.' },
            bookingModeMeta,
          },
        });
        return;
      }
      if (method === 'cash') {
        // Cash: vendor owes platform (commission + customer fee)
        const owedCents = (platformFeeCents || 0) + (createdCustomerFeeCents || 0);
        try {
          await fetch('/api/stripe?action=init', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ booking_id: bookingId, amount_cents: owedCents, currency: 'eur', direction: 'vendor_to_platform', method: 'cash' })
          });
        } catch {}
        const bookingModeMeta =
          offerCheck.data?.ok === true
            ? {
                booking_mode: offerCheck.data.booking_mode as string | undefined,
                approval_status: offerCheck.data.approval_status as string | undefined,
                vendor_response_deadline_at: offerCheck.data.vendor_response_deadline_at as string | null | undefined,
              }
            : undefined;
        navigate(`/payment-success`, {
          state: {
            bookingId,
            bookingData: data,
            orderId: `ORD-${Date.now()}`,
            company: { name: company.name, image: company.image },
            service: { name: service.name },
            pricing: { servicePrice: baseTotal, serviceFee, tax: 0, discount: 0, total: grandTotal },
            payment: { method: 'cash', status: 'pending_vendor_remit' },
            bookingModeMeta,
          },
        });
      } else {
        // Card: Checkout Session (Stripe)
        try {
          const resp = await fetch('/api/stripe?action=init', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ booking_id: bookingId, amount_cents: Math.round(grandTotal * 100), currency: 'eur', customer_email: payload.customer_email })
          });
          const initData = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            await rollbackUnpaidCardBooking();
            const code = initData?.error;
            const mode = initData?.mode;
            const friendly =
              code === 'vendor_not_ready'
                ? `Pagamenti non disponibili: l’azienda non ha completato Stripe (${mode || 'mode sconosciuta'}).`
                : null;
            const extra = initData?.message || initData?.details?.hint || null;
            const desc = [friendly || code || null, extra].filter(Boolean).join(' ');
            toast({
              title: 'Errore di pagamento',
              description: desc || 'Inizializzazione pagamento non riuscita',
              variant: 'destructive'
            });
            return;
          }
            // Zero-amount flow: backend returns ok:true, free:true and already confirms the booking
            if (initData?.free === true) {
              const bookingModeMeta =
                offerCheck.data?.ok === true
                  ? {
                      booking_mode: offerCheck.data.booking_mode as string | undefined,
                      approval_status: offerCheck.data.approval_status as string | undefined,
                      vendor_response_deadline_at: offerCheck.data.vendor_response_deadline_at as string | null | undefined,
                    }
                  : undefined;
              navigate(`/payment-success`, {
                state: {
                  bookingId,
                  bookingData: data,
                  orderId: `ORD-${Date.now()}`,
                  company: { name: company.name, image: company.image },
                  service: { name: service.name },
                  pricing: { servicePrice: baseTotal, serviceFee, tax: 0, discount: 0, total: grandTotal },
                  payment: { method: 'card', status: 'paid', free: true },
                  bookingModeMeta,
                },
              });
            return;
          }
          if (initData?.url) {
            window.location.assign(initData.url);
            return;
          }
          await rollbackUnpaidCardBooking();
          toast({ title: 'Errore di pagamento', description: 'URL di checkout non fornita', variant: 'destructive' });
          return;
        } catch (e: any) {
          await rollbackUnpaidCardBooking();
          toast({ title: 'Errore di pagamento', description: e?.message || 'Inizializzazione pagamento non riuscita', variant: 'destructive' });
          return;
        }
      }

      // NOTE: Future occurrences for recurring series will be created when vendor confirms the booking
    } catch (err: any) {
      toast({ title: "Errore", description: err.message || "Qualcosa è andato storto", variant: "destructive" });
    }
  };

  const handleSameAddressChange = (checked: boolean) => {
    form.setValue("useSameAddress", checked);
    if (checked) {
      const serviceAddress = form.getValues("serviceAddress");
      form.setValue("billingAddress", serviceAddress || "");
    }
  };

  const nextStep = async () => {
    // Validate key fields for the current step before proceeding
    if (currentStep === 1) {
      const valid = await form.trigger(["date", "time"], { shouldFocus: true });
      if (!valid) return;
      if (offerCheck.loading) {
        toast({
          title: "Verifica in corso",
          description: "Attendi il calcolo della disponibilità per la data e l'orario selezionati.",
        });
        return;
      }
      if (offerCheck.data && offerCheck.data.ok === false) {
        toast({
          title: "Data o orario non disponibili",
          description: "Scegli un'altra combinazione.",
          variant: "destructive",
        });
        return;
      }
    }
    if (currentStep === 3) {
      const fields: (keyof BookingFormData)[] = [
        "firstName", "lastName", "email", "phone", "serviceAddress"
      ];
      // Billing address required only when not using same address
      if (!useSameAddress) fields.push("billingAddress" as any);
      const valid = await form.trigger(fields as any, { shouldFocus: true });
      if (!valid) return;
    }
    if (currentStep === 2 && isRecurring) {
      const freq = form.getValues('recurringFrequency');
      const end = form.getValues('recurringEndDate');
      if (!freq || !end) {
        toast({ title: 'Ricorrenza obbligatoria', description: 'Seleziona frequenza e data di fine per le prenotazioni ricorrenti.', variant: 'destructive' });
        return;
      }
    }
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const [localPhotoFiles, setLocalPhotoFiles] = useState<File[]>([]);
  const [photoStatuses, setPhotoStatuses] = useState<{ name: string; size: number; ok: boolean; error?: string }[]>([]);
  const MAX_MB = 10;
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const nextFiles: File[] = [];
    const nextStatuses: { name: string; size: number; ok: boolean; error?: string }[] = [];
    Array.from(files).forEach((file) => {
      const sizeMb = file.size / (1024 * 1024);
      const isImg = file.type.startsWith('image/');
      if (!isImg) {
        nextStatuses.push({ name: file.name, size: file.size, ok: false, error: 'Unsupported format' });
        return;
      }
      if (sizeMb > MAX_MB) {
        nextStatuses.push({ name: file.name, size: file.size, ok: false, error: `File too large (> ${MAX_MB}MB)` });
        return;
      }
      nextFiles.push(file);
      nextStatuses.push({ name: file.name, size: file.size, ok: true });
    });
    setLocalPhotoFiles((prev) => [...prev, ...nextFiles]);
    setPhotoStatuses((prev) => [...prev, ...nextStatuses]);
    // Allow selecting the same file again by resetting the input
    try { (event.target as any).value = ''; } catch {}
  };

  return (
    <div className="min-h-screen app-page">
      <Seo title="Prenotazione | Halomora" noindex canonicalPath={`/booking/${companyId || ""}/${serviceId || ""}`} />
      {/* Header */}
      <div className="app-hero py-6">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/company/${companyId}`)}
              className="text-white hover:bg-white/10 w-full sm:w-auto justify-start"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna all'azienda
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white break-words">Prenota il servizio</h1>
              <p className="text-white/90 break-words">Programma la tua pulizia!</p>
            </div>
            <Link to="/faq" className="text-sm text-white/90 underline hover:text-white self-start sm:self-auto whitespace-nowrap">
              Serve aiuto?
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4 overflow-x-auto">
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium",
                  currentStep >= 1 ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
                )}>
                  1
                </div>
                <span className={cn(
                  "text-xs sm:text-sm font-medium",
                  currentStep >= 1 ? "text-gray-900" : "text-gray-500"
                )}>
                  Programma
                </span>
              </div>
              <div className="flex-1 h-px bg-gray-200 mx-4" />
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium",
                  currentStep >= 2 ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
                )}>
                  2
                </div>
                <span className={cn(
                  "text-xs sm:text-sm font-medium",
                  currentStep >= 2 ? "text-gray-900" : "text-gray-500"
                )}>
                  Dettagli servizio
                </span>
              </div>
              <div className="flex-1 h-px bg-gray-200 mx-4" />
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium",
                  currentStep >= 3 ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
                )}>
                  3
                </div>
                <span className={cn(
                  "text-xs sm:text-sm font-medium",
                  currentStep >= 3 ? "text-gray-900" : "text-gray-500"
                )}>
                  I tuoi dati
                </span>
              </div>
              <div className="flex-1 h-px bg-gray-200 mx-4" />
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium",
                  currentStep >= 4 ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
                )}>
                  4
                </div>
                <span className={cn(
                  "text-xs sm:text-sm font-medium",
                  currentStep >= 4 ? "text-gray-900" : "text-gray-500"
                )}>
                  Riepilogo
                </span>
              </div>
            </div>
            <Progress value={(currentStep / totalSteps) * 100} className="h-2" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <Card className="border-gray-200 shadow-card">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-gray-900">
                    {currentStep === 1 && t('booking.step.schedule','Scegli la data e l\'orario')}
                    {currentStep === 2 && t('booking.step.details','Dettagli e preferenze del servizio')}
                    {currentStep === 3 && t('booking.step.info','I tuoi dati')}
                    {currentStep === 4 && t('booking.step.review','Riepilogo e conferma')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form 
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      onSubmit={(e) => { e.preventDefault(); }}
                      className="space-y-6"
                    >
                       {/* Step 1: Schedule */}
                      {currentStep === 1 && (
                        <div className="space-y-6">
                           <PropertyProfileSelector
                             onPropertySelected={(prop) => {
                               setSelectedProperty(prop);
                               // Auto-fill service address field for visibility in step 3
                               if (prop?.address) {
                                 form.setValue("serviceAddress", prop.address);
                               }
                               setSelectedServiceAddressId(prop?.id || null);
                             }}
                           />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                              control={form.control}
                              name="date"
                              render={({ field }) => (
                                <FormItem className="flex flex-col">
                                  <FormLabel className="text-gray-700">Data servizio</FormLabel>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <FormControl>
                                        <Button
                                          variant="outline"
                                          className={cn(
                                            "w-full pl-3 text-left font-normal border-gray-300",
                                            !field.value && "text-gray-500"
                                          )}
                                        >
                                          {field.value ? (
                                            format(field.value, "PPP")
                                          ) : (
                                            <span>Seleziona una data</span>
                                          )}
                                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                        </Button>
                                      </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                        mode="single"
                                        selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => {
                                if (date < new Date() || date < new Date('1900-01-01')) return true;
                                try {
                                  const key = format(date, 'yyyy-MM-dd');
                                  const capMap = (window as any).__fh_cap_map__ || {};
                                  const entry = capMap[key] as { cap: number; count: number } | undefined;
                                  if (!entry) return false; // default selectable (cap=5 handled when loading map)
                                  return entry.cap <= 0 || entry.count >= entry.cap;
                                } catch { return false; }
                              }}
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="time"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">Orario preferito</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger className="border-gray-300">
                                        <SelectValue placeholder="Seleziona un orario" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {timeSlots.map((time) => (
                                        <SelectItem key={time} value={time}>
                                          {time}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {offerCheck.loading && (
                            <p className="text-sm text-muted-foreground">Verifica modalità e disponibilità…</p>
                          )}
                          {!offerCheck.loading && offerCheck.data?.ok === true && (
                            <Alert>
                              <AlertTitle>Modalità: {bookingModeLabel(String(offerCheck.data.booking_mode))}</AlertTitle>
                              <AlertDescription className="space-y-1">
                                {(offerCheck.data.booking_mode === "request" || offerCheck.data.booking_mode === "urgent") &&
                                  typeof offerCheck.data.vendor_response_deadline_at === "string" && (
                                    <span className="block">
                                      Risposta vendor entro:{" "}
                                      {format(new Date(String(offerCheck.data.vendor_response_deadline_at)), "PPp")}
                                    </span>
                                  )}
                                {offerCheck.data.booking_mode === "instant" && (
                                  <span className="block">Conferma immediata: nessuna approvazione vendor richiesta per questa fascia.</span>
                                )}
                                {offerCheck.data.booking_mode === "request" && (
                                  <span className="block">
                                    Richiede conferma dell’azienda; rispondi entro lo SLA indicato sopra dopo il pagamento (se applicabile).
                                  </span>
                                )}
                                {offerCheck.data.booking_mode === "urgent" && (() => {
                                  const _polUrg = (service as any)?.booking_policy?.urgent;
                                  const _strategy = String(_polUrg?.pricing_strategy || 'multiplier');
                                  const _val = Number(_polUrg?.pricing_value || 1.5);
                                  const _surchargeLabel = _strategy === 'multiplier'
                                    ? `+${Math.round((_val - 1) * 100)}%`
                                    : `+€${_val.toFixed(2)}`;
                                  return (
                                    <>
                                      <span className="block font-medium">
                                        Tempistica ravvicinata: applicato sovrapprezzo {_surchargeLabel} concordato con il fornitore.
                                      </span>
                                      <span className="block text-xs">
                                        L'azienda ha tempo limitato per accettare o rifiutare (priorità in inbox).
                                      </span>
                                    </>
                                  );
                                })()}
                              </AlertDescription>
                            </Alert>
                          )}
                          {!offerCheck.loading && offerCheck.data && offerCheck.data.ok === false && (
                            <Alert variant="destructive">
                              <AlertTitle>Non prenotabile</AlertTitle>
                              <AlertDescription>
                                {describeUnavailableReason(String(offerCheck.data.unavailable_reason || "booking_unavailable"))}
                              </AlertDescription>
                            </Alert>
                          )}
                          {watchDate && isSameDay(watchDate, new Date()) && (
                            <p className="text-xs text-muted-foreground border-l-2 border-primary/30 pl-3">
                              Stesso giorno: le prenotazioni per oggi sono accettate solo{" "}
                              <strong>prima delle 08:00</strong> (fuso orario Italia/Europa). Dopo l’orario di
                              cutoff la data odierna non è più selezionabile per nuovi ordini.
                            </p>
                          )}

                          {/* Recurring Booking Option */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                              <div className="flex items-center gap-3">
                                <Repeat className="h-5 w-5 text-primary" />
                                <div>
                                  <h3 className="font-medium text-gray-900">Prenotazione ricorrente</h3>
                                  <p className="text-sm text-gray-600">Programma pulizie ricorrenti</p>
                                </div>
                              </div>
                              <Switch
                                checked={isRecurring}
                                onCheckedChange={(checked) => {
                                  setIsRecurring(checked);
                                  form.setValue('isRecurring', checked as any);
                                }}
                              />
                            </div>

                            {isRecurring && (
                              <div className="p-4 bg-gray-50 rounded-xl space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <FormField
                                    control={form.control}
                                    name="recurringFrequency"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="text-gray-700">Frequenza</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                                          <FormControl>
                                            <SelectTrigger className="border-gray-300">
                                              <SelectValue placeholder="Seleziona frequenza" />
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            <SelectItem value="weekly">Settimanale</SelectItem>
                                            <SelectItem value="biweekly">Bisettimanale</SelectItem>
                                            <SelectItem value="monthly">Mensile</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />

                                  <FormField
                                    control={form.control}
                                    name="recurringEndDate"
                                    render={({ field }) => (
                                      <FormItem className="flex flex-col">
                                        <FormLabel className="text-gray-700">Data di fine</FormLabel>
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <FormControl>
                                              <Button
                                                variant="outline"
                                                className={cn(
                                                  "w-full pl-3 text-left font-normal border-gray-300",
                                                  !field.value && "text-gray-500"
                                                )}
                                              >
                                                {field.value ? (
                                                  format(field.value, "PPP")
                                                ) : (
                                                  <span>Nessuna data di fine</span>
                                                )}
                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                              </Button>
                                            </FormControl>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                              mode="single"
                                              selected={field.value}
                                              onSelect={field.onChange}
                                              disabled={(date) =>
                                                date < new Date() || date < new Date("1900-01-01")
                                              }
                                              initialFocus
                                            />
                                          </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Step 2: Service Details */}
                      {currentStep === 2 && (
                        <div className="space-y-6">
                          {(() => {
                            const rrStep = normalizePricingRules((service as any)?.pricing_rules || pricingRules || {});
                            const modifiersActive = arePricingRulesActive(rrStep, (service as any)?.pricing_type);
                            if (modifiersActive) return null;
                            return (
                              <div className="text-sm text-gray-600 p-3 border rounded-lg bg-muted/30">
                                {t(
                                  'booking.fixed_price_explain',
                                  "Questo servizio e offerto a prezzo fisso: non serve specificare dimensione o tipologia. Se hai esigenze particolari, scrivilo nelle istruzioni speciali qui sotto."
                                )}
                              </div>
                            );
                          })()}
                          {arePricingRulesActive(normalizePricingRules((service as any)?.pricing_rules || pricingRules || {}), (service as any)?.pricing_type) && (<>
                          {/* Service Type Selection */}
                          <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900">{t('booking.service_type','Tipologia di servizio')}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getServiceTypeOptions().length > 0
                                ? getServiceTypeOptions().map((serviceType) => (
                                      <Card
                                        key={serviceType.value}
                                        className={cn(
                                          "cursor-pointer border-2 transition-all",
                                          selectedServiceType === serviceType.value
                                            ? "border-primary bg-primary/5"
                                            : "border-gray-200 hover:border-gray-300"
                                        )}
                                        onClick={() => {
                                          setSelectedServiceType(serviceType.value);
                                          try { form.setValue('serviceType', serviceType.value); } catch {}
                                        }}
                                      >
                                        <CardContent className="p-4">
                                          <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-medium text-gray-900">{serviceType.label}</h4>
                                            <span className="font-bold text-primary">
                                              {serviceType.multiplier && serviceType.multiplier !== 1
                                                ? `+${Math.round((serviceType.multiplier - 1) * 100)}%`
                                                : "Standard"}
                                            </span>
                                          </div>
                                          <p className="text-sm text-gray-600">{serviceType.description}</p>
                                        </CardContent>
                                      </Card>
                                    ))
                                : (
                                  <div className="text-sm text-gray-600">Le tipologie non sono configurate per questo servizio.</div>
                                )}
                            </div>
                          </div>

                          {/* Request a Quote CTA — gated by QUOTES_ENABLED */}
                          {QUOTES_ENABLED && (
                            <div className="space-y-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full border-gray-300 text-sm leading-snug whitespace-normal py-3 flex items-center justify-center text-center"
                                title="Usa questa opzione quando le opzioni disponibili non corrispondono alle tue esigenze (es. ambito personalizzato, materiali speciali, dimensioni atipiche). Il venditore risponderà con un preventivo e tempistiche."
                                onClick={() => setQuoteOpen(true)}
                              >
                                <MessageSquare className="h-4 w-4 mr-2 shrink-0" />
                                <span className="break-words">{t('booking.request_quote',"Non trovi ciò che ti serve? Richiedi un preventivo")}</span>
                              </Button>
                              <p className="text-xs text-gray-500">
                                Quando usarla: se nessuna delle tipologie elencate copre le tue esigenze o serve una combinazione personalizzata. Soggetto ad approvazione del venditore: riceverai prezzo e date disponibili.
                              </p>
                            </div>
                          )}

                          {/* Square Meters */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-gray-900">{t('booking.property_size','Dimensione immobile')}</h3>
                              <span className="text-lg font-bold text-primary">{squareMeters[0]} m²</span>
                            </div>
                            {(() => {
                              const rr = normalizePricingRules((service as any)?.pricing_rules || pricingRules || {});
                              return (
                                <Slider
                                  value={squareMeters}
                                    onValueChange={(v) => { setSquareMeters(v); try { form.setValue('squareMeters', v?.[0] || 0); } catch {} }}
                                  max={rr.sqm.max}
                                  min={rr.sqm.min}
                                  step={rr.sqm.step}
                                  className="w-full"
                                />
                              );
                            })()}
                            <div className="flex justify-between text-sm text-gray-600">
                              {(() => {
                                const rr = normalizePricingRules((service as any)?.pricing_rules || pricingRules || {});
                                return (
                                  <>
                                    <span>{rr.sqm.min} m²</span>
                                    <span>{rr.sqm.max} m²</span>
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          </>)}
                          {/* The manual "Livello di urgenza" radio used
                              to live here. It was redundant with the
                              server-resolved booking_mode (which already
                              deduces urgent vs standard from the date/time)
                              and caused the price to jump unexpectedly. The
                              urgency surcharge is now applied automatically
                              and shown in the price breakdown with a clear
                              "Tempistica ravvicinata" label explaining why. */}

                          {/* Special Instructions */}
                          <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900">{t('booking.special_instructions','Istruzioni speciali')}</h3>
                            <FormField
                              control={form.control}
                              name="specialInstructions"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Textarea
                                      placeholder="Richieste particolari, allergie o note..."
                                      className="resize-none border-gray-300"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Photo Upload */}
                          <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900">{t('booking.upload_photos','Carica foto (opzionale)')}</h3>
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-gray-600 mb-2">
                                Carica foto per aiutarci a capire le tue esigenze di pulizia
                              </p>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleFileUpload}
                                className="hidden"
                                id="photo-upload"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                className="border-gray-300"
                                onClick={() => {
                                  const el = document.getElementById('photo-upload') as HTMLInputElement | null;
                                  el?.click();
                                }}
                              >
                                Scegli file
                              </Button>
                            </div>
                            {photoStatuses.length > 0 && (
                              <div className="mt-3 space-y-2 text-sm">
                                {photoStatuses.map((p, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 border rounded">
                                    <div className="truncate max-w-[70%]">
                                      {p.name} <span className="text-xs text-gray-500">({Math.round(p.size/1024)} KB)</span>
                                    </div>
                                    <div className={p.ok ? 'text-green-600' : 'text-red-600'}>
                                      {p.ok ? 'Pronto' : p.error}
                                    </div>
                                  </div>
                                ))}
                                <div className="text-xs text-gray-500">Dimensione massima: {MAX_MB}MB. Solo immagini.</div>
                              </div>
                            )}
                              </div>
                          <div className="mt-2 p-3 border rounded-lg bg-white">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-gray-700">Per visita</div>
                                  <div className="font-semibold text-gray-900">{formatCurrency(calculatePrice())}</div>
                                </div>
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                  <div>Occorrenze</div>
                                  <div>{getOccurrencesCount()}</div>
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                  <div className="text-sm font-medium text-gray-900">Totale serie</div>
                                  <div className="text-primary font-bold">{formatCurrency(getSeriesTotal())}</div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Addebitato per visita al momento del servizio.</div>
                              </div>
                        </div>
                      )}

                      {/* Step 3: Details */}
                      {currentStep === 3 && (
                        <div className="space-y-6">
                          <div className="flex items-center justify-between p-4 border rounded-xl">
                            <div>
                              <h3 className="font-medium text-gray-900">Checkout come ospite</h3>
                              <p className="text-sm text-gray-600">Nessun account? Prenota con i tuoi dati.</p>
                            </div>
                            <Switch
                              checked={guestMode}
                              onCheckedChange={(checked) => {
                                setGuestMode(checked);
                                const method = form.getValues().paymentMethod;
                                if (checked && (method === 'cash' || method === 'invoice')) {
                                  form.setValue('paymentMethod', 'card');
                                  form.setValue('requestInvoice', false);
                                  toast({
                                    title: GUEST_OFFLINE_PAYMENT_TITLE,
                                    description: GUEST_OFFLINE_PAYMENT_DESCRIPTION,
                                    variant: 'destructive'
                                  });
                                }
                              }}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                              control={form.control}
                              name="firstName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">Nome</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="lastName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">Cognome</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                              control={form.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">Email</FormLabel>
                                  <FormControl>
                                    <Input type="email" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">Telefono</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name="serviceAddress"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700">Indirizzo del servizio</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="useSameAddress"
                              checked={useSameAddress}
                              onCheckedChange={handleSameAddressChange}
                            />
                            <label
                              htmlFor="useSameAddress"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Usa lo stesso indirizzo per la fatturazione
                            </label>
                          </div>

                          {!useSameAddress && (
                            <FormField
                              control={form.control}
                              name="billingAddress"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">Indirizzo di fatturazione</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      )}

                       {/* Step 4: Review */}
                      {currentStep === 4 && (
                        <div className="space-y-6">
                          <div className="bg-gray-50 rounded-xl p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">
                              {t('booking.summary','Riepilogo prenotazione')}
                            </h3>
                            <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                              <div className="text-gray-600">Servizio</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {service.name}
                              </div>
                              <div className="text-gray-600">Tipologia</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {getServiceTypeOptions().find(s => s.value === selectedServiceType)?.label}
                              </div>
                              <div className="text-gray-600">Data</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {form.watch('date') ? format(form.watch('date')!, 'PPP') : 'Non selezionato'}
                              </div>
                              <div className="text-gray-600">Orario</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {form.watch('time') || 'Non selezionato'}
                              </div>
                              <div className="text-gray-600">Dimensione</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {squareMeters[0]} m²
                              </div>
                              <div className="text-gray-600">Modalità prenotazione</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {offerCheck.data?.ok === true && typeof offerCheck.data.booking_mode === "string"
                                  ? bookingModeLabel(String(offerCheck.data.booking_mode))
                                  : getUrgencyOptions().find((u) => u.value === selectedUrgency)?.label}
                              </div>
                              {offerCheck.data?.ok === true &&
                                (offerCheck.data.booking_mode === "request" || offerCheck.data.booking_mode === "urgent") &&
                                typeof offerCheck.data.vendor_response_deadline_at === "string" && (
                                  <>
                                    <div className="text-gray-600">Risposta vendor</div>
                                    <div className="text-gray-900 font-medium text-right break-words">
                                      entro{" "}
                                      {format(
                                        new Date(String(offerCheck.data.vendor_response_deadline_at)),
                                        "PPp"
                                      )}
                                    </div>
                                  </>
                                )}
                              {isRecurring && (
                                <>
                                  <div className="text-gray-600">Ricorrente</div>
                                  <div className="text-gray-900 font-medium text-right">Sì</div>
                                  <div className="text-gray-600">Occorrenze</div>
                                  <div className="text-gray-900 font-medium text-right">{getOccurrencesCount()}</div>
                                  <div className="text-gray-900/70">Totale serie</div>
                                  <div className="text-gray-900 font-medium text-right">{formatCurrency(getSeriesTotal())}</div>
                                </>
                              )}
                              <div className="text-gray-600">Nome</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {form.watch('firstName')} {form.watch('lastName')}
                              </div>
                              <div className="text-gray-600">Email</div>
                              <div className="text-gray-900 font-medium text-right break-words">
                                {form.watch('email')}
                              </div>
                            </div>
                            <div className="border-t mt-4 pt-3" />
                              <div className="flex flex-col gap-3">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <span className="text-gray-600">Metodo di pagamento</span>
                                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 items-start sm:items-center">
                                    <label className="text-sm text-gray-700 flex items-center gap-1 break-words">
                                      <input type="radio" name="pm" checked={form.watch('paymentMethod') === 'card'} onChange={() => {
                                        const accepted = (service?.accepted_payment_methods || []) as string[];
                                        if (accepted.length > 0 && !accepted.includes('card')) {
                                          toast({ title: 'Metodo non permesso', description: 'Il metodo di pagamento selezionato non è permesso dal venditore, contatta il venditore per maggiori informazioni', variant: 'destructive' });
                                          return;
                                        }
                                        form.setValue('paymentMethod','card');
                                      }} /> Carta
                                    </label>
                                    <label className="text-sm text-gray-700 flex items-center gap-1 break-words">
                                      <input type="radio" name="pm" checked={form.watch('paymentMethod') === 'cash'} onChange={() => {
                                        if (guestMode) {
                                          toast({ title: GUEST_OFFLINE_PAYMENT_TITLE, description: GUEST_OFFLINE_PAYMENT_DESCRIPTION, variant: 'destructive' });
                                          return;
                                        }
                                        const accepted = (service?.accepted_payment_methods || []) as string[];
                                        if (accepted.length > 0 && !accepted.includes('cash')) {
                                          toast({ title: 'Metodo non permesso', description: 'Il metodo di pagamento selezionato non è permesso dal venditore, contatta il venditore per maggiori informazioni', variant: 'destructive' });
                                          return;
                                        }
                                        form.setValue('paymentMethod','cash');
                                      }} /> Contanti al venditore
                                    </label>
                                    <label className="text-sm text-gray-700 flex items-center gap-1 break-words">
                                      <input type="radio" name="pm" checked={form.watch('paymentMethod') === 'invoice'} onChange={() => {
                                        if (guestMode) {
                                          toast({ title: GUEST_OFFLINE_PAYMENT_TITLE, description: GUEST_OFFLINE_PAYMENT_DESCRIPTION, variant: 'destructive' });
                                          return;
                                        }
                                        const accepted = (service?.accepted_payment_methods || []) as string[];
                                        if (accepted.length > 0 && !accepted.includes('invoice')) {
                                          toast({ title: 'Metodo non permesso', description: 'Il venditore non accetta pagamento a fattura per questo servizio.', variant: 'destructive' });
                                          return;
                                        }
                                        form.setValue('paymentMethod','invoice');
                                        form.setValue('requestInvoice', true);
                                      }} /> Solo Fattura (bonifico dopo fattura)
                                    </label>
                                  </div>
                                </div>
                                {form.watch('paymentMethod') !== 'invoice' && (
                                  <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={!!form.watch('requestInvoice')}
                                      onChange={(e) => form.setValue('requestInvoice', e.target.checked)}
                                    />
                                    <span>Richiedi fattura (dati per emissione fattura obbligatori)</span>
                                  </label>
                                )}
                                {(form.watch('paymentMethod') === 'invoice' || form.watch('requestInvoice')) && (
                                  <FormField
                                    control={form.control}
                                    name="billingInfo"
                                    render={({ field: _field }) => (
                                      <FormItem>
                                        <div className="mt-3 p-4 border rounded-lg bg-muted/30">
                                          <p className="text-sm font-medium text-gray-700 mb-3">Dati per la fattura</p>
                                          <InvoiceBillingForm showSaveToProfile={!guestMode} />
                                        </div>
                                        <FormMessage className="mt-2 text-destructive" />
                                      </FormItem>
                                    )}
                                  />
                                )}
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Prezzo servizio</span>
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(isRecurring ? getSeriesTotal() : calculatePrice())}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Fee di pagamento/piattaforma</span>
                                <span className="font-medium text-gray-900">
                                  {service?.vendor_covers_customer_fee
                                    ? formatCurrency(0)
                                    : (() => {
                                        const base = isRecurring ? getSeriesTotal() : calculatePrice();
                                        return formatCurrency(Math.min(Math.round(base * 0.02 * 100) / 100, 10));
                                      })()}
                                </span>
                              </div>
                              {(() => {
                                const baseUsed = isRecurring ? getSeriesTotal() : calculatePrice();
                                const fee = service?.vendor_covers_customer_fee
                                  ? 0
                                  : Math.min(Math.round(baseUsed * 0.02 * 100) / 100, 10);
                                const total = baseUsed + fee;
                                return (
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-lg">
                                      <span className="text-gray-900 font-semibold">
                                        {form.watch("paymentMethod") === "card" ? "Totale dovuto oggi" : "Totale servizio"}
                                      </span>
                                      <span className="text-primary font-bold">{formatCurrency(total)}</span>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                      {form.watch("paymentMethod") === "card"
                                        ? "Halomora processa il pagamento per conto dell'azienda che eroga il servizio. Il totale include il prezzo del servizio e l'eventuale fee cliente indicata sopra."
                                        : "Halomora non incassa questo importo oggi: per pagamenti in contanti o a fattura il pagamento avviene direttamente con l'azienda che eroga il servizio, secondo il metodo scelto al checkout."}
                                    </p>
                                    {(offerCheck.data?.booking_mode === "request" || offerCheck.data?.booking_mode === "urgent") && (
                                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
                                        Se il venditore rifiuta o non risponde entro lo SLA, il pagamento con carta viene rimborsato quando previsto dalla policy del servizio. Contanti e fattura: nessun addebito carta tramite Halomora.
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            <PriceBreakdownAccordion
                              basePrice={Number(service?.base_price || 0)}
                              sizeSqm={squareMeters[0]}
                              serviceType={selectedServiceType}
                              pricingType={(service as any)?.pricing_type}
                              pricingRules={(service as any)?.pricing_rules || pricingRules || {}}
                              bookingPolicy={(service as any)?.booking_policy}
                              resolvedBookingMode={
                                offerCheck.data?.ok === true && typeof offerCheck.data.booking_mode === "string"
                                  ? (offerCheck.data.booking_mode as "instant" | "request" | "urgent")
                                  : null
                              }
                              discountAmount={Math.max(0, getRawPrice() - calculatePrice())}
                              customerPaymentFee={
                                service?.vendor_covers_customer_fee
                                  ? 0
                                  : Math.min(
                                      Math.round((isRecurring ? getSeriesTotal() : calculatePrice()) * 0.02 * 100) / 100,
                                      10
                                    )
                              }
                              occurrences={isRecurring ? getOccurrencesCount() : 1}
                            />
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="pt-6">
                        <div className="flex justify-between">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={prevStep}
                            disabled={currentStep === 1}
                            className="border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            {t('booking.btn.prev','Indietro')}
                          </Button>
                          
                          {currentStep < totalSteps ? (
                            <Button
                              type="button"
                              onClick={nextStep}
                              className="bg-primary hover:bg-primary/90"
                            >
                              {t('booking.btn.next','Avanti')}
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              className="bg-primary hover:bg-primary/90"
                              disabled={form.formState.isSubmitting || !acceptedPaymentDisclosure}
                              onClick={form.handleSubmit(onSubmit, (errors) => {
                                const errorKeys = Object.keys(errors);
                                if (errorKeys.some(k => ["date","time"].includes(k))) { setCurrentStep(1); return; }
                                if (errorKeys.some(k => ["firstName","lastName","email","phone","serviceAddress","billingAddress"].includes(k))) { setCurrentStep(3); return; }
                                if (errorKeys.includes("billingInfo") && (errors as any).billingInfo?.message) {
                                  toast({
                                    title: "Dati fattura non validi",
                                    description: (errors as any).billingInfo.message,
                                    variant: "destructive",
                                  });
                                }
                              })}
                            >
                              {form.formState.isSubmitting ? 'Elaborazione…' : t('booking.btn.confirm','Conferma prenotazione')}
                            </Button>
                          )}
                        </div>
                        {currentStep === totalSteps && (
                          <div className="mt-3 space-y-2">
                            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                              <Checkbox
                                id="payment-disclosure"
                                checked={acceptedPaymentDisclosure}
                                onCheckedChange={(checked) => setAcceptedPaymentDisclosure(checked === true)}
                              />
                              <Label htmlFor="payment-disclosure" className="text-xs leading-5 text-gray-600">
                                {form.watch("paymentMethod") === "card" ? (
                                  <>
                                    Confermando, accetti che il pagamento includa il prezzo del servizio e le eventuali fee indicate, e che eventuali rimborsi/penali seguano le condizioni mostrate nel checkout e nei{" "}
                                    <Link to="/legal/terms" className="text-primary underline">Termini</Link>.
                                  </>
                                ) : (
                                  <>
                                    Confermando, accetti il prezzo del servizio indicato e che eventuali rimborsi, penali o obblighi di pagamento seguano le condizioni mostrate nel checkout e nei{" "}
                                    <Link to="/legal/terms" className="text-primary underline">Termini</Link>.
                                  </>
                                )}
                              </Label>
                            </div>
                            <p className="text-center text-xs text-gray-500">Pagamento sicuro</p>
                          </div>
                        )}
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Company Info */}
              <Card className="border-gray-200 shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">Dettagli servizio</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={company.image}
                      alt={company.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div>
                      <h3 className="font-medium text-gray-900">{company.name}</h3>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-current text-yellow-400" />
                          <span className="text-sm text-gray-600">{company.rating}</span>
                        </div>
                        <span className="text-sm text-gray-600">({company.reviews} recensioni)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{service.name}</h4>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Prezzo stimato (per visita):</span>
                      <div className="text-right">
                        {(() => { const cur = getApplicableOffer(); return cur; })() ? (
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm line-through text-gray-500">{formatCurrency(getRawPrice())}</span>
                            <span className="font-bold text-lg text-gray-900">{formatCurrency(calculatePrice())}</span>
                          </div>
                        ) : (
                          <span className="font-bold text-lg text-gray-900">{formatCurrency(calculatePrice())}</span>
                        )}
                        {isRecurring && (
                          <div className="text-xs text-gray-600 mt-1">
                            Totale serie: <span className="font-medium text-gray-900">{formatCurrency(getSeriesTotal())}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {offers.length > 0 && (
                      <div className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Offerta stagionale</div>
                            {(() => {
                              const cur = getSelectedOffer();
                              if (!cur) return <div className="text-xs text-gray-600">Disponibile</div>;
                              return (
                                <div className="text-xs text-gray-600">
                                  {cur.name} (-{cur.discount_percent}%) • {cur.start_date} → {cur.end_date}
                                </div>
                              );
                            })()}
                          </div>
                          {offers.length > 1 ? (
                            <Select value={selectedOfferId || undefined} onValueChange={(v) => setSelectedOfferId(v === 'none' ? null : v)}>
                              <SelectTrigger className="w-[200px] border-gray-300">
                                <SelectValue placeholder="Seleziona offerta" />
                              </SelectTrigger>
                              <SelectContent>
                                {offers.filter(Boolean).map((o:any) => (
                                  <SelectItem key={o.id} value={o.id}>{o.name} (-{o.discount_percent}%)</SelectItem>
                                ))}
                                <SelectItem value="none">Nessuna offerta</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            (() => { const cur = getSelectedOffer() || offers[0]; return (
                              <Badge variant="secondary" className="bg-green-100 text-green-700">-{cur?.discount_percent || 0}%</Badge>
                            ); })()
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Dettaglio prezzo: la breakdown sotto al riepilogo (step 4) e' la
                  fonte unica di verita'. La vecchia card laterale generava prezzi
                  divergenti per i recurring e duplicava informazioni gia' presenti
                  nell'accordion principale, quindi e' stata rimossa. */}

              {/* What's Included */}
              <Card className="border-gray-200 shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">Cosa è incluso</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(service.included_items || []).length === 0 ? (
                      <div className="text-sm text-gray-600">Nessun dettaglio disponibile</div>
                    ) : (
                      (service.included_items || []).map((txt: string, idx: number) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-accent-green-500" />
                          <span className="text-sm text-gray-700">{txt}</span>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Company Benefits */}
              <Card className="border-gray-200 shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">Perché scegliere {company.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(service.benefit_items || []).length === 0 ? (
                    <div className="text-sm text-gray-600">Nessun vantaggio disponibile</div>
                  ) : (
                    (service.benefit_items || []).map((txt: string, idx: number) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-accent-green-500" />
                        <span className="text-sm text-gray-700">{txt}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>


        {/* Request Quote Modal — gated by QUOTES_ENABLED */}
        {QUOTES_ENABLED && quoteOpen && (
          <div className="app-modal-overlay">
            <div className="app-modal-panel w-full max-w-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Richiedi un preventivo</h3>
                <button onClick={() => setQuoteOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Superficie indicativa (m²) - opzionale</Label>
                  <Input type="number" value={quoteSqm || ''} onChange={(e) => setQuoteSqm(parseInt(e.target.value) || undefined)} />
                </div>
                <div>
                  <Label>Note</Label>
                  <Textarea value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} placeholder="Descrivi ciò di cui hai bisogno..." />
                </div>
                <div>
                  <Label>Foto (opzionale)</Label>
                  <input type="file" multiple accept="image/*" onChange={(e) => setQuoteFiles(e.target.files ? Array.from(e.target.files) : [])} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setQuoteOpen(false)} className="border-gray-300">Annulla</Button>
                <Button onClick={async () => {
                  try {
                    const { data: sessionData } = await supabase.auth.getSession();
                    const session = sessionData.session;
                    if (!session) { toast({ title: 'Accesso richiesto', description: 'Accedi per richiedere un preventivo', variant: 'destructive' }); return; }
                    if (!session) { toast({ title: 'Accesso richiesto', description: 'Accedi per richiedere un preventivo', variant: 'destructive' }); return; }
                    const compId = company?.id || companyId;
                    const svcId = service?.id || serviceId;
                    const prof = await supabase.from('profiles').select('id').eq('user_id', session.user.id).maybeSingle();
                    const customerId = prof.data?.id;
                    const ins = await (supabase as any).from('quote_requests').insert({
                      customer_id: customerId,
                      company_id: compId,
                      service_id: svcId,
                      square_meters: quoteSqm || null,
                      special_requests: quoteNotes || null,
                      status: 'pending'
                    }).select('id').single();
                    if (ins.error) throw ins.error;
                    const reqId = ins.data.id;
                    // Upload photos
                    for (const file of quoteFiles) {
                      const path = `${reqId}/${Date.now()}_${file.name}`;
                      const up = await (supabase as any).storage.from('quote-photos').upload(path, file, { upsert: false });
                      // Optionally store path in a JSON array column if present
                    }
                    // Notify vendor
                    try {
                      await fetch('/api/notifications/enqueue', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ template: 'quote_submitted', to_role: 'vendor', payload: {
                          company_id: compId,
                          service_id: svcId,
                          customer_email: session.user.email,
                          customer_name: form.getValues('firstName') + ' ' + form.getValues('lastName'),
                          notes: quoteNotes
                        }})
                      });
                    } catch {}
                    toast({ title: 'Richiesta inviata', description: 'La tua richiesta di preventivo è stata inviata.' });
                    setQuoteOpen(false);
                    setQuoteFiles([]);
                    setQuoteNotes('');
                    setQuoteSqm(undefined);
                  } catch (e: any) {
                    toast({ title: 'Errore', description: e.message || 'Invio richiesta preventivo non riuscito', variant: 'destructive' });
                    toast({ title: 'Errore', description: e.message || 'Invio richiesta preventivo non riuscito', variant: 'destructive' });
                  }
                }}>Invia richiesta</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingForm;